import { useMemo, useState } from 'react';

import DashboardLayout from '@/components/layout/DashboardLayout';
import PageHeader from '@/components/ui/PageHeader';
import { adminSidebar } from '@/config/sidebarConfig';
import {
  type CreateBreachDto,
  type DataBreach,
  type GetBreachesParams,
  type UpdateBreachDto,
  closeBreachApi,
  createBreachApi,
  exportBreachesApi,
  getBreachesApi,
  notifyBreachCnilApi,
  notifyBreachSubjectsApi,
  updateBreachApi,
} from '@/lib/api/gdprApi';
import { useAuthStore } from '@/stores/auth-store';
import { BreachCause, BreachStatus, BreachType, RiskLevel } from '@shared/enums/gdpr.enum';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Edit, ExternalLink, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

// ─── Badge configs ────────────────────────────────────────────
const RISK_CONFIG: Record<string, { label: string; classes: string }> = {
  [RiskLevel.LOW]: { label: 'Faible', classes: 'bg-emerald-100 text-emerald-700' },
  [RiskLevel.MEDIUM]: { label: 'Moyen', classes: 'bg-amber-100 text-amber-700' },
  [RiskLevel.HIGH]: { label: 'Élevé', classes: 'bg-red-100 text-red-700' },
};

const STATUS_CONFIG: Record<string, { label: string; classes: string }> = {
  [BreachStatus.OPEN]: { label: 'Ouvert', classes: 'bg-red-100 text-red-700' },
  [BreachStatus.CONTAINED]: { label: 'Contenu', classes: 'bg-amber-100 text-amber-700' },
  [BreachStatus.CLOSED]: { label: 'Clos', classes: 'bg-emerald-100 text-emerald-700' },
};

const BREACH_TYPE_LABELS: Record<string, string> = {
  [BreachType.CONFIDENTIALITY]: 'Confidentialité',
  [BreachType.INTEGRITY]: 'Intégrité',
  [BreachType.AVAILABILITY]: 'Disponibilité',
};

const BREACH_CAUSE_LABELS: Record<string, string> = {
  [BreachCause.INTERNAL_ERROR]: 'Erreur interne',
  [BreachCause.EXTERNAL_ATTACK]: 'Attaque externe',
  [BreachCause.LOST_DEVICE]: "Perte d'appareil",
  [BreachCause.MISCONFIGURATION]: 'Mauvaise configuration',
  [BreachCause.THIRD_PARTY]: 'Prestataire tiers',
  [BreachCause.OTHER]: 'Autre',
};

function RiskBadge({ value }: { value: string }) {
  const config = RISK_CONFIG[value] ?? { label: value, classes: 'bg-slate-100 text-slate-700' };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${config.classes}`}>{config.label}</span>;
}

function StatusBadge({ value }: { value: string }) {
  const config = STATUS_CONFIG[value] ?? { label: value, classes: 'bg-slate-100 text-slate-700' };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${config.classes}`}>{config.label}</span>;
}

// ─── CNIL notification modal ──────────────────────────────────
function CnilModal({
  breach,
  onClose,
  onConfirm,
  isPending,
}: {
  breach: DataBreach;
  onClose: () => void;
  onConfirm: (ref: string) => void;
  isPending: boolean;
}) {
  const [cnilRef, setCnilRef] = useState('');

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
      <div className='w-full max-w-md rounded-xl bg-white shadow-2xl'>
        <div className='flex items-center justify-between px-6 py-4 border-b border-slate-200'>
          <h2 className='text-lg font-bold text-slate-900'>Notification CNIL</h2>
          <button onClick={onClose} className='rounded p-1 text-slate-500 hover:bg-slate-100'>
            <X className='h-5 w-5' />
          </button>
        </div>
        <div className='px-6 py-4 space-y-4'>
          <p className='text-sm text-slate-600'>
            Violation <strong>#{breach.id}</strong> — {breach.description.slice(0, 80)}
          </p>
          <a
            href='https://notifications.cnil.fr/notifications/index'
            target='_blank'
            rel='noopener noreferrer'
            className='flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100'
          >
            <ExternalLink className='h-4 w-4' />
            Ouvrir le portail CNIL
          </a>
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Référence de notification CNIL</label>
            <input
              type='text'
              value={cnilRef}
              onChange={e => setCnilRef(e.target.value)}
              placeholder='ex: 2024-12345-CNIL'
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>
        </div>
        <div className='flex justify-end gap-3 px-6 py-4 border-t border-slate-200'>
          <button
            onClick={onClose}
            className='rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50'
          >
            Annuler
          </button>
          <button
            onClick={() => onConfirm(cnilRef)}
            disabled={!cnilRef.trim() || isPending}
            className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
          >
            {isPending ? 'Enregistrement...' : 'Confirmer la notification'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Notify Subjects modal ────────────────────────────────────
function NotifySubjectsModal({
  breach,
  onClose,
  onConfirm,
  isPending,
}: {
  breach: DataBreach;
  onClose: () => void;
  onConfirm: (method: string) => void;
  isPending: boolean;
}) {
  const [method, setMethod] = useState('email');

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
      <div className='w-full max-w-md rounded-xl bg-white shadow-2xl'>
        <div className='flex items-center justify-between px-6 py-4 border-b border-slate-200'>
          <h2 className='text-lg font-bold text-slate-900'>Notification des personnes concernées</h2>
          <button onClick={onClose} className='rounded p-1 text-slate-500 hover:bg-slate-100'>
            <X className='h-5 w-5' />
          </button>
        </div>
        <div className='px-6 py-4 space-y-4'>
          <p className='text-sm text-slate-600'>
            Violation <strong>#{breach.id}</strong>
          </p>
          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Méthode de notification</label>
            <select
              value={method}
              onChange={e => setMethod(e.target.value)}
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value='email'>Email</option>
              <option value='courrier'>Courrier postal</option>
              <option value='telephone'>Téléphone</option>
              <option value='autre'>Autre</option>
            </select>
          </div>
        </div>
        <div className='flex justify-end gap-3 px-6 py-4 border-t border-slate-200'>
          <button
            onClick={onClose}
            className='rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50'
          >
            Annuler
          </button>
          <button
            onClick={() => onConfirm(method)}
            disabled={isPending}
            className='rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50'
          >
            {isPending ? 'Enregistrement...' : 'Confirmer la notification'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Breach Form Modal ────────────────────────────────────────
const EMPTY_BREACH_FORM: CreateBreachDto = {
  detectedAt: new Date().toISOString().slice(0, 16),
  occurredAt: null,
  breachType: BreachType.CONFIDENTIALITY,
  cause: BreachCause.INTERNAL_ERROR,
  description: '',
  affectedData: [],
  affectedCount: null,
  riskLevel: RiskLevel.MEDIUM,
  cnilNotificationRequired: false,
  containmentActions: null,
};

interface BreachFormModalProps {
  initial?: DataBreach | null;
  onClose: () => void;
  onSave: (data: CreateBreachDto | UpdateBreachDto) => void;
  isSaving: boolean;
}

function BreachFormModal({ initial, onClose, onSave, isSaving }: BreachFormModalProps) {
  const [form, setForm] = useState<CreateBreachDto>(() =>
    initial
      ? {
          detectedAt: initial.detectedAt.slice(0, 16),
          occurredAt: initial.occurredAt ? initial.occurredAt.slice(0, 16) : null,
          breachType: initial.breachType,
          cause: initial.cause,
          description: initial.description,
          affectedData: initial.affectedData,
          affectedCount: initial.affectedCount ?? null,
          riskLevel: initial.riskLevel,
          cnilNotificationRequired: initial.cnilNotificationRequired,
          containmentActions: initial.containmentActions ?? null,
        }
      : EMPTY_BREACH_FORM,
  );

  const [affectedDataRaw, setAffectedDataRaw] = useState(() => JSON.stringify(form.affectedData, null, 2));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    let affectedData: string[];
    try {
      affectedData = JSON.parse(affectedDataRaw) as string[];
    } catch {
      toast.error('Format JSON invalide pour les données affectées');
      return;
    }
    if (!Array.isArray(affectedData)) {
      toast.error('Les données affectées doivent être un tableau JSON');
      return;
    }
    onSave({ ...form, affectedData });
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
      <div className='w-full max-w-2xl rounded-xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto'>
        <div className='sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-200'>
          <h2 className='text-lg font-bold text-slate-900'>
            {initial ? 'Modifier la violation' : 'Déclarer une violation'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className='px-6 py-4 space-y-4'>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-slate-700 mb-1'>
                Date de détection <span className='text-red-500'>*</span>
              </label>
              <input
                type='datetime-local'
                required
                value={form.detectedAt}
                onChange={e => setForm(f => ({ ...f, detectedAt: e.target.value }))}
                className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-slate-700 mb-1'>Date de survenance</label>
              <input
                type='datetime-local'
                value={form.occurredAt ?? ''}
                onChange={e => setForm(f => ({ ...f, occurredAt: e.target.value || null }))}
                className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
            </div>
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-slate-700 mb-1'>
                Type de violation <span className='text-red-500'>*</span>
              </label>
              <select
                value={form.breachType}
                onChange={e => setForm(f => ({ ...f, breachType: e.target.value }))}
                className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              >
                {Object.entries(BREACH_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className='block text-sm font-medium text-slate-700 mb-1'>
                Cause <span className='text-red-500'>*</span>
              </label>
              <select
                value={form.cause}
                onChange={e => setForm(f => ({ ...f, cause: e.target.value }))}
                className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              >
                {Object.entries(BREACH_CAUSE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>
              Description <span className='text-red-500'>*</span>
            </label>
            <textarea
              required
              rows={3}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Données affectées (tableau JSON)</label>
            <textarea
              rows={3}
              value={affectedDataRaw}
              onChange={e => setAffectedDataRaw(e.target.value)}
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500'
              placeholder='["email", "adresse IP"]'
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-slate-700 mb-1'>
                Niveau de risque <span className='text-red-500'>*</span>
              </label>
              <select
                value={form.riskLevel}
                onChange={e => setForm(f => ({ ...f, riskLevel: e.target.value }))}
                className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              >
                {Object.entries(RISK_CONFIG).map(([value, { label }]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className='block text-sm font-medium text-slate-700 mb-1'>Nombre de personnes affectées</label>
              <input
                type='number'
                min={0}
                value={form.affectedCount ?? ''}
                onChange={e => setForm(f => ({ ...f, affectedCount: e.target.value ? Number(e.target.value) : null }))}
                className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
            </div>
          </div>

          <div className='flex items-center gap-3'>
            <input
              type='checkbox'
              id='cnilRequired'
              checked={form.cnilNotificationRequired}
              onChange={e => setForm(f => ({ ...f, cnilNotificationRequired: e.target.checked }))}
              className='h-4 w-4 rounded border-slate-300'
            />
            <label htmlFor='cnilRequired' className='text-sm font-medium text-slate-700'>
              Notification CNIL requise (Art. 33 RGPD)
            </label>
          </div>

          <div>
            <label className='block text-sm font-medium text-slate-700 mb-1'>Mesures de confinement</label>
            <textarea
              rows={2}
              value={form.containmentActions ?? ''}
              onChange={e => setForm(f => ({ ...f, containmentActions: e.target.value || null }))}
              className='w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          <div className='flex justify-end gap-3 pt-2 border-t border-slate-200'>
            <button
              type='button'
              onClick={onClose}
              className='rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50'
            >
              Annuler
            </button>
            <button
              type='submit'
              disabled={isSaving}
              className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
            >
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────
export default function AdminGdprBreaches() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterRisk, setFilterRisk] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DataBreach | null>(null);
  const [cnilTarget, setCnilTarget] = useState<DataBreach | null>(null);
  const [notifySubjectsTarget, setNotifySubjectsTarget] = useState<DataBreach | null>(null);

  const queryParams: GetBreachesParams = {
    page,
    limit: 20,
    ...(filterStatus && { status: filterStatus as GetBreachesParams['status'] }),
    ...(filterRisk && { riskLevel: filterRisk as GetBreachesParams['riskLevel'] }),
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'gdpr', 'breaches', queryParams],
    queryFn: () => getBreachesApi(queryParams),
    enabled: isAuthenticated,
  });

  const breaches = useMemo<DataBreach[]>(() => data?.data ?? [], [data]);
  const meta = data?.meta;
  const isPageLoading = isLoading || !isAuthenticated;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'gdpr', 'breaches'] });

  const createMutation = useMutation({
    mutationFn: (dto: CreateBreachDto) => createBreachApi(dto),
    onSuccess: () => {
      toast.success('Violation déclarée');
      void invalidate();
      setModalOpen(false);
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur inconnue'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateBreachDto }) => updateBreachApi(id, dto),
    onSuccess: () => {
      toast.success('Violation mise à jour');
      void invalidate();
      setModalOpen(false);
      setEditTarget(null);
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur inconnue'),
  });

  const cnilMutation = useMutation({
    mutationFn: ({ id, ref }: { id: number; ref: string }) => notifyBreachCnilApi(id, ref),
    onSuccess: () => {
      toast.success('Notification CNIL enregistrée');
      void invalidate();
      setCnilTarget(null);
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur inconnue'),
  });

  const notifySubjectsMutation = useMutation({
    mutationFn: ({ id, method }: { id: number; method: string }) => notifyBreachSubjectsApi(id, method),
    onSuccess: () => {
      toast.success('Notification des personnes enregistrée');
      void invalidate();
      setNotifySubjectsTarget(null);
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur inconnue'),
  });

  const closeMutation = useMutation({
    mutationFn: (id: number) => closeBreachApi(id),
    onSuccess: () => {
      toast.success('Violation clôturée');
      void invalidate();
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur inconnue'),
  });

  function handleExport() {
    exportBreachesApi()
      .then(rows => {
        const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gdpr-breaches-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err: unknown) => toast.error(err instanceof Error ? err.message : 'Export échoué'));
  }

  function handleSave(dto: CreateBreachDto | UpdateBreachDto) {
    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, dto });
    } else {
      createMutation.mutate(dto as CreateBreachDto);
    }
  }

  function handleClose(b: DataBreach) {
    if (!window.confirm(`Clôturer la violation #${b.id} ?`)) return;
    closeMutation.mutate(b.id);
  }

  function resetFilters() {
    setFilterStatus('');
    setFilterRisk('');
    setPage(1);
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='p-8 space-y-6'>
        <PageHeader
          title='Cahier des violations (Art. 33.5 RGPD)'
          subtitle='Registre de toutes les violations de données personnelles'
          actions={
            <div className='flex gap-3'>
              <button
                onClick={handleExport}
                className='flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50'
              >
                <Download className='h-4 w-4' />
                Exporter JSON
              </button>
              <button
                onClick={() => {
                  setEditTarget(null);
                  setModalOpen(true);
                }}
                className='flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700'
              >
                <Plus className='h-4 w-4' />
                Déclarer une violation
              </button>
            </div>
          }
        />

        {/* Filters */}
        <div className='mb-4 flex flex-wrap items-end gap-3'>
          <div>
            <label className='block text-xs font-medium text-slate-600 mb-1'>Statut</label>
            <select
              value={filterStatus}
              onChange={e => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              className='rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value=''>Tous les statuts</option>
              {Object.entries(STATUS_CONFIG).map(([value, { label }]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className='block text-xs font-medium text-slate-600 mb-1'>Niveau de risque</label>
            <select
              value={filterRisk}
              onChange={e => {
                setFilterRisk(e.target.value);
                setPage(1);
              }}
              className='rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value=''>Tous les niveaux</option>
              {Object.entries(RISK_CONFIG).map(([value, { label }]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {(filterStatus || filterRisk) && (
            <button
              onClick={resetFilters}
              className='rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50'
            >
              Réinitialiser
            </button>
          )}
        </div>

        {isPageLoading && (
          <div className='flex items-center justify-center py-20'>
            <div className='h-8 w-8 animate-spin rounded-full border-4 border-red-600 border-t-transparent' />
          </div>
        )}

        {isError && !isPageLoading && (
          <div className='rounded-lg bg-red-50 border border-red-200 p-4 text-red-700'>
            Erreur lors du chargement des violations.
          </div>
        )}

        {!isPageLoading && !isError && (
          <>
            <div className='overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-slate-200 bg-slate-50'>
                    <th className='px-4 py-3 text-left font-semibold text-slate-700'>Date</th>
                    <th className='px-4 py-3 text-left font-semibold text-slate-700'>Type</th>
                    <th className='px-4 py-3 text-left font-semibold text-slate-700'>Risque</th>
                    <th className='px-4 py-3 text-left font-semibold text-slate-700'>Statut</th>
                    <th className='px-4 py-3 text-left font-semibold text-slate-700'>CNIL</th>
                    <th className='px-4 py-3 text-right font-semibold text-slate-700'>Actions</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  {breaches.length === 0 && (
                    <tr>
                      <td colSpan={6} className='px-4 py-10 text-center text-slate-500'>
                        Aucune violation enregistrée.
                      </td>
                    </tr>
                  )}
                  {breaches.map(b => (
                    <tr key={b.id} className='hover:bg-slate-50/50'>
                      <td className='px-4 py-3 text-slate-700'>{new Date(b.detectedAt).toLocaleDateString('fr-FR')}</td>
                      <td className='px-4 py-3'>
                        <p className='font-medium text-slate-900'>{BREACH_TYPE_LABELS[b.breachType] ?? b.breachType}</p>
                        <p className='text-xs text-slate-500 line-clamp-1'>{b.description}</p>
                      </td>
                      <td className='px-4 py-3'>
                        <RiskBadge value={b.riskLevel} />
                      </td>
                      <td className='px-4 py-3'>
                        <StatusBadge value={b.status} />
                      </td>
                      <td className='px-4 py-3'>
                        {b.cnilNotificationRequired ? (
                          b.cnilNotifiedAt ? (
                            <span className='rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700'>
                              Notifiée
                            </span>
                          ) : (
                            <span className='rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700'>
                              En attente
                            </span>
                          )
                        ) : (
                          <span className='text-xs text-slate-400'>Non requise</span>
                        )}
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex justify-end gap-1'>
                          {/* Notify CNIL */}
                          {b.cnilNotificationRequired && !b.cnilNotifiedAt && (
                            <button
                              title='Notifier la CNIL'
                              onClick={() => setCnilTarget(b)}
                              className='rounded p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-600 text-xs font-medium px-2'
                            >
                              CNIL
                            </button>
                          )}
                          {/* Notify subjects */}
                          {!b.subjectsNotifiedAt && b.status !== BreachStatus.CLOSED && (
                            <button
                              title='Notifier les personnes concernées'
                              onClick={() => setNotifySubjectsTarget(b)}
                              className='rounded p-1.5 text-slate-500 hover:bg-amber-50 hover:text-amber-600 text-xs font-medium px-2'
                            >
                              Notifier
                            </button>
                          )}
                          {/* Edit */}
                          <button
                            title='Modifier'
                            onClick={() => {
                              setEditTarget(b);
                              setModalOpen(true);
                            }}
                            className='rounded p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-600'
                          >
                            <Edit className='h-4 w-4' />
                          </button>
                          {/* Close */}
                          {b.status !== BreachStatus.CLOSED && (
                            <button
                              title='Clôturer'
                              onClick={() => handleClose(b)}
                              disabled={closeMutation.isPending}
                              className='rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600'
                            >
                              <Trash2 className='h-4 w-4' />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta && meta.totalPages > 1 && (
              <div className='mt-4 flex items-center justify-between'>
                <p className='text-sm text-slate-600'>
                  {meta.total} violation{meta.total > 1 ? 's' : ''} au total
                </p>
                <div className='flex gap-2'>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className='rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40'
                  >
                    Précédent
                  </button>
                  <span className='flex items-center px-3 text-sm text-slate-600'>
                    {page} / {meta.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(meta.totalPages, p + 1))}
                    disabled={page >= meta.totalPages}
                    className='rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40'
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Modals */}
        {modalOpen && (
          <BreachFormModal
            initial={editTarget}
            onClose={() => {
              setModalOpen(false);
              setEditTarget(null);
            }}
            onSave={handleSave}
            isSaving={isSaving}
          />
        )}

        {cnilTarget && (
          <CnilModal
            breach={cnilTarget}
            onClose={() => setCnilTarget(null)}
            onConfirm={ref => cnilMutation.mutate({ id: cnilTarget.id, ref })}
            isPending={cnilMutation.isPending}
          />
        )}

        {notifySubjectsTarget && (
          <NotifySubjectsModal
            breach={notifySubjectsTarget}
            onClose={() => setNotifySubjectsTarget(null)}
            onConfirm={method => notifySubjectsMutation.mutate({ id: notifySubjectsTarget.id, method })}
            isPending={notifySubjectsMutation.isPending}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
